import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';

const ACTIVE_LOCATION_KEY = 'spirara.activeLocationId';

const WorkspaceContext = createContext({
  business: null,
  locations: [],
  activeLocation: null,
  setActiveLocationId: () => {},
  loading: true,
  error: null,
  badges: {},
  refresh: () => {},
});

export const useWorkspace = () => useContext(WorkspaceContext);

export const WorkspaceProvider = ({ children }) => {
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [locations, setLocations] = useState([]);
  const [activeLocationId, setActiveLocationIdState] = useState(
    () => localStorage.getItem(ACTIVE_LOCATION_KEY) || null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user) {
      setBusiness(null);
      setLocations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const result = await api.getWorkspace();
    if (result.error) {
      setError(result.error);
      setBusiness(null);
      setLocations([]);
    } else {
      setError(null);
      setBusiness(result.data?.business || null);
      setLocations(result.data?.locations || []);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const setActiveLocationId = useCallback((locationId) => {
    setActiveLocationIdState(locationId);
    if (locationId) {
      localStorage.setItem(ACTIVE_LOCATION_KEY, locationId);
    } else {
      localStorage.removeItem(ACTIVE_LOCATION_KEY);
    }
  }, []);

  // The active location falls back to the default (first) location when the
  // stored id no longer exists (e.g. removed location, different account).
  const activeLocation = useMemo(() => {
    if (locations.length === 0) return null;
    return (
      locations.find((location) => location.id === activeLocationId) ||
      locations.find((location) => location.isDefault) ||
      locations[0]
    );
  }, [locations, activeLocationId]);

  return (
    <WorkspaceContext.Provider
      value={{
        business,
        locations,
        activeLocation,
        setActiveLocationId,
        loading,
        error,
        // Per-tab notification counts, keyed by primaryNav id. Populated once each
        // tab has real data behind it.
        badges: {},
        refresh: load,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
